import { useEffect, useRef, useState } from "react"
import debounce from "lodash/debounce"
import cx from "classnames"

import { Spinner } from "../../../components/Spinner"
import SavedProjectsStacksCard from "./SavedProjectsStacksCard"
import { SavedStackType } from "../types"
import { ProgramType } from "@/types/program"

export interface SavedProjectsStacksItemProps {
  stackKey: SavedStackType | null
  title: string
  timestamp: string
  image: string
  active: SavedStackType | null
  expanded: SavedStackType | null
  onToggle: (stackKey: SavedStackType | null) => void
  programs: ProgramType[]
  isLoading: boolean
}

const SavedProjectsStacksItem = ({
  stackKey,
  title,
  timestamp,
  image,
  active,
  expanded,
  onToggle,
  programs,
  isLoading,
}: SavedProjectsStacksItemProps) => {
  const [maxHeight, setMaxHeight] = useState<number>(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const isExpanded = expanded === stackKey
  const isActive = active === stackKey

  useEffect(() => {
    const updateHeight = () => {
      if (isExpanded && containerRef.current) {
        setMaxHeight(containerRef.current.scrollHeight)
      }
    }

    const debouncedResizeHandler = debounce(updateHeight, 250)

    updateHeight()

    window.addEventListener("resize", debouncedResizeHandler)

    return () => {
      window.removeEventListener("resize", debouncedResizeHandler)
      debouncedResizeHandler.cancel()
    }
  }, [isExpanded])

  const stackTrackingClass = `${stackKey}-stack`

  return (
    <>
      <button
        type="button"
        aria-expanded={isExpanded}
        onClick={() => onToggle(stackKey)}
        className={cx(
          stackTrackingClass,
          "flex flex-col items-center select-none min-w-full hover:cursor-pointer pt-3 px-3 pb-2 mb-6 !ring-0"
        )}
        data-testid="stack-button"
      >
        <figure className="w-full">
          <img
            src={image}
            alt={title}
            className="w-full max-w-[467px] h-auto block"
          />
        </figure>

        <span
          className={cx(
            "w-full text-lg text-left font-display font-semibold leading-[140%] tracking-[0.4px] px-3",
            {
              "text-leaf": isActive,
            }
          )}
        >
          {title}
        </span>

        <span className="w-full text-base text-left font-body leading-[140%] tracking-[0.32px] px-3">
          Created {timestamp}
        </span>
      </button>

      <div
        ref={containerRef}
        role="region"
        aria-labelledby={title}
        className={cx(
          "flex flex-col-reverse col-span-full relative h-full overflow-hidden transition-all ease-in-out",
          "w-[calc(100%+40px)] md:w-[calc(100%+96px)] lg:w-[calc(100%+128px)] xl:w-[calc(100%+144px)]",
          "-left-[20px] md:-left-[48px] lg:-left-[64px] xl:-left-[72px]",
          "before:bg-dusk-100 before:content-[''] before:absolute before:z-[2] before:top-0 before:h-[2px] before:left-0 before:right-0 before:[box-shadow:0px_1px_2px_rgba(0,0,0,0.06),_0px_1px_3px_rgba(0,0,0,0.10)]",
          "after:bg-dusk-100 after:content-[''] after:absolute after:bottom-6 after:h-[2px] after:left-0 after:right-0 after:[box-shadow:0px_-1px_3px_rgba(0,0,0,0.10),_0px_-1px_2px_rgba(0,0,0,0.06)]",
          {
            "duration-1000": isExpanded,
            "duration-750": !isExpanded,
          }
        )}
        style={{
          maxHeight: isExpanded ? maxHeight : 0,
        }}
      >
        <div className="bg-[#F5F5F5] border-b-24 border-b-white px-[20px] md:px-[48px] lg:px-[64px] xl:px-[72px]">
          {isLoading ? (
            <div className="relative flex-auto flex items-center justify-center h-[482px]">
              <Spinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 4xl:grid-cols-4 gap-6 pt-6">
              {programs.map((program) => (
                <SavedProjectsStacksCard
                  key={program.id}
                  programData={program}
                />
              ))}

              <div className="bg-transparent col-span-full text-center">
                <button
                  type="button"
                  className="link relative font-body font-bold leading-[130%] tracking-[0.32px] mb-[14px] hover:cursor-pointer"
                  onClick={() => onToggle(null)}
                >
                  Close Stack
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default SavedProjectsStacksItem
